import { AuditLogRequestError } from "../interfaces";

/** Used when a caught value has neither a real `code` nor `message` -- e.g. a thrown network
 * `Error`, a plain string, or anything else that isn't the api's own `{ code, message }` body. */
export const FALLBACK_REQUEST_ERROR: AuditLogRequestError = { code: 0, message: "Request failed" };

/**
 * Narrows a caught value to the shape the api's error responses actually have, replacing a blind
 * `error as AuditLogRequestError` cast -- which would happily "type" any thrown value (a network
 * `Error`, a string, `undefined`) as a well-formed api error payload with no runtime check.
 *
 * @param value - whatever a saga's catch block caught.
 * @returns true if `value` has the numeric `code` and string `message` fields the api's error
 *   payload always carries.
 */
export const isAuditLogRequestError = (value: unknown): value is AuditLogRequestError =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as { code?: unknown }).code === "number" &&
  typeof (value as { message?: unknown }).message === "string";

/**
 * The saga-facing entry point: whatever was caught, in, a well-formed `AuditLogRequestError` out --
 * either the value itself (narrowed) or `FALLBACK_REQUEST_ERROR`.
 *
 * @param error - whatever a saga's catch block caught.
 * @returns a real `AuditLogRequestError`, never a pass-through of an unrecognised shape.
 */
export const toAuditLogRequestError = (error: unknown): AuditLogRequestError =>
  isAuditLogRequestError(error) ? error : FALLBACK_REQUEST_ERROR;
